import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminActionModal } from '../../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../../components/admin-shell/AdminShell';
import { adminGet, adminPost } from '../_api';

type VerificationDetail = {
  verification: {
    id: string;
    subject_kind: 'artist' | 'host_user' | 'venue';
    subject_id: string;
    subject_label: string;
    subject_user_id: string | null;
    state: 'pending' | 'approved' | 'rejected' | 'revoked';
    submitted_at: string;
    evidence_urls: string[] | null;
    evidence_kinds: string[] | null;
    reviewed_by_user_id: string | null;
    reviewed_by_label: string | null;
    reviewed_at: string | null;
    rejection_reason: string | null;
    revocation_reason: string | null;
    revoked_at: string | null;
    expires_at: string | null;
    internal_notes: string | null;
    created_at: string;
    updated_at: string;
  };
};

export const Route = createFileRoute('/admin/verifications/$id')({
  component: AdminVerificationDetail,
});

function AdminVerificationDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'verifications', id],
    queryFn: () => adminGet<VerificationDetail>(`/api/admin/verifications/${id}`),
  });

  const [approveOpen, approveCtl] = useDisclosure(false);
  const [rejectOpen, rejectCtl] = useDisclosure(false);
  const [revokeOpen, revokeCtl] = useDisclosure(false);
  const [reason, setReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  // Optional expiry. Mantine 9 DatePickerInput hands back an ISO yyyy-mm-dd
  // string (or null). Empty = one-time check that never expires (the default).
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/verifications/${id}/approve`, {
        verification_id: id,
        ...(internalNotes.trim().length > 0 ? { internal_notes: internalNotes } : {}),
        // expires_at wants a full ISO timestamp with offset; widen the picked
        // date (UTC midnight) so the server's isoTimestampSchema accepts it.
        ...(expiresAt !== null ? { expires_at: new Date(expiresAt).toISOString() } : {}),
      }),
    onSuccess: () => {
      approveCtl.close();
      setInternalNotes('');
      setExpiresAt(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'verifications', id] });
    },
  });

  const reject = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/verifications/${id}/reject`, {
        verification_id: id,
        rejection_reason: reason,
        ...(internalNotes.trim().length > 0 ? { internal_notes: internalNotes } : {}),
      }),
    onSuccess: () => {
      rejectCtl.close();
      setReason('');
      setInternalNotes('');
      void qc.invalidateQueries({ queryKey: ['admin', 'verifications', id] });
    },
  });

  const revoke = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/verifications/${id}/revoke`, {
        verification_id: id,
        revocation_reason: reason,
        ...(internalNotes.trim().length > 0 ? { internal_notes: internalNotes } : {}),
      }),
    onSuccess: () => {
      revokeCtl.close();
      setReason('');
      setInternalNotes('');
      void qc.invalidateQueries({ queryKey: ['admin', 'verifications', id] });
    },
  });

  if (isLoading) {
    return (
      <AdminShell breadcrumb="Verifications">
        <Loader size="sm" />
      </AdminShell>
    );
  }
  if (error || !data) {
    return (
      <AdminShell breadcrumb="Verifications">
        <Alert color="red">Could not load this verification.</Alert>
      </AdminShell>
    );
  }

  const v = data.verification;

  return (
    <AdminShell breadcrumb={`Verifications / ${v.subject_label}`}>
      <Stack gap="lg">
        <Anchor component={Link} to="/admin/verifications" size="sm">
          ← Queue
        </Anchor>
        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Title order={2}>{v.subject_label}</Title>
            <Group gap="xs">
              <Badge variant="light">{subjectKindLabel(v.subject_kind)}</Badge>
              {v.state === 'revoked' ? (
                <Tooltip
                  label="Verification was revoked by an admin or lapsed past its expiry."
                  withArrow
                >
                  <Badge variant="light" color={stateColor(v.state)}>
                    {stateLabel(v.state)}
                  </Badge>
                </Tooltip>
              ) : (
                <Badge variant="light" color={stateColor(v.state)}>
                  {stateLabel(v.state)}
                </Badge>
              )}
            </Group>
          </Stack>
          <Group>
            {v.state === 'pending' ? (
              <>
                <Button color="red" variant="light" onClick={rejectCtl.open}>
                  Reject
                </Button>
                <Button color="tide" onClick={approveCtl.open}>
                  Verify
                </Button>
              </>
            ) : null}
            {v.state === 'approved' ? (
              <Button color="yellow" variant="light" onClick={revokeCtl.open}>
                Revoke
              </Button>
            ) : null}
          </Group>
        </Group>

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            Evidence
          </Title>
          {v.evidence_urls && v.evidence_urls.length > 0 ? (
            <Stack gap={6}>
              {v.evidence_urls.map((url, idx) => (
                <Group key={url} gap="sm">
                  <Badge variant="light">{v.evidence_kinds?.[idx] ?? 'other'}</Badge>
                  <Anchor href={evidenceHref(url)} target="_blank" rel="noopener">
                    {evidenceLabel(url)}
                  </Anchor>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed">No evidence attached.</Text>
          )}
        </Card>

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            Timeline
          </Title>
          <Stack gap={2}>
            <KV k="Submitted" v={formatDate(v.submitted_at)} />
            {v.reviewed_at ? <KV k="Reviewed" v={formatDate(v.reviewed_at)} /> : null}
            {(v.reviewed_by_label ?? v.reviewed_by_user_id) !== null ? (
              <KV k="Reviewed by" v={v.reviewed_by_label ?? v.reviewed_by_user_id ?? '—'} />
            ) : null}
            {v.expires_at ? <KV k="Expires" v={formatDate(v.expires_at)} /> : null}
            {v.rejection_reason ? <KV k="Rejection reason" v={v.rejection_reason} /> : null}
            {v.revocation_reason ? <KV k="Revocation reason" v={v.revocation_reason} /> : null}
            {v.internal_notes ? <KV k="Internal notes" v={v.internal_notes} /> : null}
          </Stack>
        </Card>
      </Stack>

      <AdminActionModal
        opened={approveOpen}
        onClose={approveCtl.close}
        title="Verify subject"
        onConfirm={() => approve.mutate()}
        confirmLabel="Verify"
        confirmColor="tide"
        loading={approve.isPending}
        error={approve.error instanceof Error ? approve.error.message : null}
      >
        <Text size="sm" c="dimmed">
          Marks this {v.subject_kind === 'venue' ? 'venue' : 'account'} as Verified. Evidence is
          purged once you confirm — only the fact of the check is kept.
        </Text>
        <DatePickerInput
          label="Expiry (optional)"
          description="Leave empty for a one-time check that doesn't expire."
          placeholder="No expiry"
          value={expiresAt}
          onChange={setExpiresAt}
          minDate={todayIso()}
          clearable
        />
        <Textarea
          label="Internal notes (optional)"
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.currentTarget.value)}
          minRows={2}
        />
      </AdminActionModal>

      <AdminActionModal
        opened={rejectOpen}
        onClose={rejectCtl.close}
        title="Reject verification"
        onConfirm={() => reject.mutate()}
        confirmLabel="Reject"
        confirmColor="red"
        loading={reject.isPending}
        error={reject.error instanceof Error ? reject.error.message : null}
        confirmDisabled={reason.trim().length < 10}
      >
        <Textarea
          label="Reason (shown to subject)"
          description="At least 10 characters. Be specific about what they need to fix."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
        />
        <Textarea
          label="Internal notes (optional)"
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.currentTarget.value)}
          minRows={2}
        />
      </AdminActionModal>

      <AdminActionModal
        opened={revokeOpen}
        onClose={revokeCtl.close}
        title="Revoke verification"
        onConfirm={() => revoke.mutate()}
        confirmLabel="Revoke"
        confirmColor="yellow"
        loading={revoke.isPending}
        error={revoke.error instanceof Error ? revoke.error.message : null}
        confirmDisabled={reason.trim().length < 10}
      >
        <Textarea
          label="Reason (shown to subject)"
          description="At least 10 characters. Logged in audit."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
        />
        <Textarea
          label="Internal notes (optional)"
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.currentTarget.value)}
          minRows={2}
        />
      </AdminActionModal>
    </AdminShell>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <Group gap="xs" align="flex-start">
      <Text c="dimmed" size="sm" style={{ minWidth: 140 }}>
        {k}
      </Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {v}
      </Text>
    </Group>
  );
}

function stateColor(state: string): string {
  if (state === 'approved') return 'tide';
  if (state === 'rejected') return 'red';
  if (state === 'revoked') return 'yellow';
  return 'blue';
}

// Spec §5 badge language: 'approved' reads as "Verified" across the admin UI.
// Underlying state value is unchanged; this is display only.
function stateLabel(state: string): string {
  if (state === 'approved') return 'Verified';
  if (state === 'pending') return 'Pending';
  if (state === 'rejected') return 'Rejected';
  if (state === 'revoked') return 'Revoked';
  return state;
}

function subjectKindLabel(kind: 'artist' | 'host_user' | 'venue'): string {
  if (kind === 'artist') return 'Artist';
  if (kind === 'host_user') return 'Host';
  return 'Venue';
}

// Mantine 9 DatePickerInput's minDate wants an ISO yyyy-mm-dd string. Block
// past dates so an expiry can only be set into the future.
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Rewrites stored R2 evidence URLs (which may be storage-key references like
// `verification/<id>/<key>` or full https URLs) to the gated streamer endpoint.
// Bare keys → /api/admin/evidence/<key>; full URLs pass through.
function evidenceHref(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return url;
  return `/api/admin/evidence/${url.replace(/^\//, '')}`;
}

function evidenceLabel(url: string): string {
  const parts = url.split('/');
  return parts[parts.length - 1] || url;
}
